import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ASSISTANT_UPLOAD_TERMINAL_STATUSES,
  type AssistantUpload,
} from '@xtreed/academy-shared';
import { onUploadSettled } from '@/lib/assistant-uploader';
import { assistantService } from '@/services/assistant.service';
import { useAssistantStore, type LocalUploadState } from '@/stores/assistant';
import { assistantKeys } from './assistantKeys';

const POLL_MS = 5000;

export interface AssistantUploadView extends AssistantUpload {
  local: LocalUploadState | null;
}

export function isUploadSettled(upload: AssistantUpload) {
  return ASSISTANT_UPLOAD_TERMINAL_STATUSES.includes(upload.status);
}

export function useAssistantUploads(conversationId: string | null) {
  const queryClient = useQueryClient();
  const localUploads = useAssistantStore((state) => state.localUploads);

  const query = useQuery({
    queryKey: assistantKeys.uploads(conversationId ?? 'none'),
    queryFn: () => assistantService.uploads(conversationId as string),
    enabled: Boolean(conversationId),
    refetchInterval: (current) =>
      current.state.data?.some((upload) => !isUploadSettled(upload))
        ? POLL_MS
        : false,
  });

  useEffect(
    () =>
      onUploadSettled(() => {
        if (!conversationId) return;
        queryClient.invalidateQueries({
          queryKey: assistantKeys.uploads(conversationId),
        });
      }),
    [conversationId, queryClient],
  );

  const uploads: AssistantUploadView[] = (query.data ?? []).map((upload) => ({
    ...upload,
    local: localUploads[upload.id] ?? null,
  }));

  return { ...query, uploads };
}
